
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FormattedProduct } from "@/types/product";
import { getCategoryNameById } from "@/hooks/use-categories";
import { getSupplierNameById } from "@/lib/utils";
import { isProduction } from "@/utils/environment";

export const useTopSellingProducts = (limit = 4, supplierId?: string) => {
  const [products, setProducts] = useState<FormattedProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopSellingProducts = async () => {
      try {
        setLoading(true);
        
        // Determine if we should filter by test status based on environment
        const testMode = !isProduction();
        
        // First try to get the top selling products using our database function
        const { data, error } = await supabase.rpc('get_top_selling_products', { 
          supplier_id: supplierId || null,
          limit_count: limit 
        });
        
        if (error) {
          throw error;
        }
        
        if (data && data.length > 0) {
          // Get supplier details for products
          const productIds = data.map(product => product.product_id);
          
          // Get supplier info for these products
          const { data: productsWithSuppliers, error: suppliersError } = await supabase
            .from('products')
            .select(`
              id, 
              supplier_id,
              category_id,
              name,
              description,
              price,
              msrp,
              case_quantity,
              total_views,
              supplier:supplier_id (
                id,
                name,
                business_name,
                is_test
              )
            `)
            .in('id', productIds);
            
          if (suppliersError) {
            throw suppliersError;
          }
          
          // Filter out test/non-test suppliers based on environment
          const filteredProducts = productsWithSuppliers.filter(product => {
            const isTestSupplier = product.supplier?.is_test || false;
            return isTestSupplier === testMode;
          });
          
          // If we don't have enough products after filtering, we'll handle that later
          
          // Format the products
          const formattedProducts = await Promise.all(
            filteredProducts.map(async (product) => {
              // Find the corresponding data from our RPC call
              const productData = data.find(p => p.product_id === product.id);
              
              if (!productData) return null;
              
              const categoryName = await getCategoryNameById(product.category_id);
              
              const supplierName = product.supplier 
                ? (product.supplier.business_name || product.supplier.name)
                : await getSupplierNameById(product.supplier_id);
              
              return {
                id: product.id,
                name: product.name,
                brand: supplierName,
                description: product.description || "",
                price: product.price,
                image: productData.image_url || "/placeholder.svg",
                category: categoryName,
                categoryId: product.category_id,
                tags: [],
                supplierId: product.supplier_id,
                msrp: product.msrp || undefined,
                caseQuantity: product.case_quantity || 1,
                totalViews: product.total_views,
                totalQuantitySold: productData.total_quantity,
                isTestSupplier: product.supplier?.is_test || false
              } as FormattedProduct;
            })
          );
          
          // Remove any null products
          const validProducts = formattedProducts.filter(Boolean) as FormattedProduct[];
          
          setProducts(validProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching top selling products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopSellingProducts();
  }, [limit, supplierId]);

  return { products, loading };
};
